import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { ArrowLeft, Printer, GraduationCap, BookOpen, Zap, Award, Flame, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";

export default function FamilyProgressReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/educator/students"),
      api.get("/educator/dashboard")
    ]).then(([sRes, dRes]) => {
      setStudents(sRes.data.students || []);
      setDashboard(dRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="progress-report-page">
      {/* Screen-only header */}
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white" data-testid="report-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">Progress Report</h1>
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-report-btn">
            <Printer size={16} className="mr-2" /> Print
          </Button>
        </div>
      </header>

      {/* Printable Report */}
      <div className="max-w-4xl mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="bg-white text-black p-10 rounded-2xl print:rounded-none print:shadow-none shadow-2xl" data-testid="report-document">

          {/* Header */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-[#F7931A]">
            <div className="flex items-center gap-3">
              <Award style={{ color: "#F7931A" }} size={32} />
              <div>
                <h1 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "bold", color: "#1a1a1a" }}>
                  Bitcoin Brain
                </h1>
                <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#888" }}>
                  Family Progress Report
                </p>
              </div>
            </div>
            <div className="text-right">
              <p style={{ fontSize: "13px", color: "#666" }}>Report Date</p>
              <p style={{ fontSize: "15px", fontWeight: "bold", color: "#1a1a1a" }}>{today}</p>
              <p style={{ fontSize: "12px", color: "#888" }}>Educator: {user?.first_name || user?.email}</p>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="text-center p-3 border border-gray-200 rounded-lg">
              <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>
                {students.length}
              </p>
              <p style={{ fontSize: "11px", color: "#888" }}>Total Students</p>
            </div>
            <div className="text-center p-3 border border-gray-200 rounded-lg">
              <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>
                {dashboard?.active_students || 0}
              </p>
              <p style={{ fontSize: "11px", color: "#888" }}>Active Learners</p>
            </div>
            <div className="text-center p-3 border border-gray-200 rounded-lg">
              <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>
                {(dashboard?.total_student_xp || 0).toLocaleString()}
              </p>
              <p style={{ fontSize: "11px", color: "#888" }}>Combined BTC XP</p>
            </div>
            <div className="text-center p-3 border border-gray-200 rounded-lg">
              <p style={{ fontSize: "24px", fontWeight: "bold", color: "#F7931A", fontFamily: "monospace" }}>
                {dashboard?.completion_rate || 0}%
              </p>
              <p style={{ fontSize: "11px", color: "#888" }}>Avg Completion</p>
            </div>
          </div>

          {/* Individual Student Reports */}
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "18px", fontWeight: "bold", color: "#1a1a1a", marginBottom: "16px" }}>
            Individual Student Progress
          </h2>

          {students.length === 0 ? (
            <p style={{ color: "#888", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>
              No students linked yet. Add students from the Educator Dashboard to generate progress reports.
            </p>
          ) : (
            <div className="space-y-6">
              {students.map((s, i) => {
                const registered = s.progress?.registered;
                const sections = s.progress?.sections_completed || 0;
                const pct = Math.round((sections / 21) * 100);
                const xp = s.progress?.total_xp || 0;
                const badges = s.progress?.badges_count || 0;
                const streak = s.progress?.streak || 0;

                return (
                  <div key={s.id} className="border border-gray-200 rounded-lg p-5 break-inside-avoid" data-testid={`report-student-${i}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 style={{ fontSize: "16px", fontWeight: "bold", color: "#1a1a1a" }}>
                          {s.student_name}
                        </h3>
                        <p style={{ fontSize: "12px", color: "#888" }}>{s.student_email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded ${registered ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {registered ? "Active" : "Invited"}
                      </span>
                    </div>

                    {registered ? (
                      <>
                        {/* Progress bar */}
                        <div className="mb-4">
                          <div className="flex justify-between text-xs mb-1">
                            <span style={{ color: "#666" }}>Course Progress</span>
                            <span style={{ color: "#F7931A", fontWeight: "bold" }}>{pct}% ({sections}/21 sections)</span>
                          </div>
                          <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: "#F7931A" }} />
                          </div>
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-4 gap-3">
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p style={{ fontSize: "16px", fontWeight: "bold", color: "#1a1a1a", fontFamily: "monospace" }}>{sections}/21</p>
                            <p style={{ fontSize: "10px", color: "#888" }}>Sections</p>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p style={{ fontSize: "16px", fontWeight: "bold", color: "#1a1a1a", fontFamily: "monospace" }}>{xp.toLocaleString()}</p>
                            <p style={{ fontSize: "10px", color: "#888" }}>BTC XP</p>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p style={{ fontSize: "16px", fontWeight: "bold", color: "#1a1a1a", fontFamily: "monospace" }}>{badges}</p>
                            <p style={{ fontSize: "10px", color: "#888" }}>Badges</p>
                          </div>
                          <div className="text-center p-2 bg-gray-50 rounded">
                            <p style={{ fontSize: "16px", fontWeight: "bold", color: "#1a1a1a", fontFamily: "monospace" }}>{streak}</p>
                            <p style={{ fontSize: "10px", color: "#888" }}>Day Streak</p>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p style={{ fontSize: "13px", color: "#888", fontStyle: "italic" }}>
                        Waiting for {s.student_name} to create an account at bitcoinbrain.space using {s.student_email}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-4 border-t border-gray-200 flex justify-between items-end">
            <div>
              <p style={{ fontSize: "11px", color: "#bbb" }}>Generated by Bitcoin Brain — bitcoinbrain.space</p>
              <p style={{ fontSize: "11px", color: "#bbb" }}>For educational portfolio documentation purposes</p>
            </div>
            <div className="text-right">
              <div style={{ borderBottom: "1px solid #ccc", width: "180px", marginBottom: "4px" }} />
              <p style={{ fontSize: "11px", color: "#888" }}>Educator Signature</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
